import { S } from '../core/game';
import { DAY_ORDER, type AffectionKey } from '../core/state';
import { m } from '../i18n';
import { npcName } from '../i18n/labels';
import { sfx } from '../audio/sfx';
import { hobbyLevel } from '../content/clsPlan';
import { dayNumber } from '../content/passport';
import { h } from './dom';
import { openPanel, refreshPanel } from './panel';

/**
 * The growth record (成長紀錄): how close you are to family and classmates, the
 * moments that happened (good choices, mistakes and making up), and hobbies.
 */

type Entry = { day: number; icon: string; key: string; arg?: string };

/** Localised text of a journal entry: `classroom.journalKeys`, or a dotted path into any namespace. */
export function journalText(e: Entry): string {
  const keys = m().classroom.journalKeys as Record<string, ((a?: string) => string) | undefined>;
  const arg = e.arg ? npcName(e.arg) : undefined;
  const f = keys[e.key];
  if (f) return f(arg);
  const walk = (path: string[]) => {
    let v: unknown = m();
    for (const part of path) v = v && typeof v === 'object' ? (v as Record<string, unknown>)[part] : undefined;
    return v;
  };
  const parts = e.key.split('.');
  // `sky.quakeSafe` may live at `sky.journal.quakeSafe`.
  let v = walk(parts);
  if (v === undefined && parts.length === 2) v = walk([parts[0], 'journal', parts[1]]);
  if (typeof v === 'function') return String((v as (a?: string) => string)(arg));
  if (typeof v === 'string') return v;
  return arg ? `${e.key} (${arg})` : e.key;
}

export function todayEntries(): Entry[] {
  const d = dayNumber();
  return S().journal.filter((j) => j.day === d);
}

function dayLabel(day: number): string {
  const week = Math.floor(day / DAY_ORDER.length);
  const id = DAY_ORDER[((day % DAY_ORDER.length) + DAY_ORDER.length) % DAY_ORDER.length];
  return m().classroom.ui.dayLabel(week, m().days[id].name);
}

const FAMILY: readonly [AffectionKey, string][] = [['mom', '👩'], ['dad', '👨'], ['sister', '👧']];
const FRIENDS: readonly [AffectionKey, string][] = [
  ['emma', '👱‍♀️'], ['xiaoyu', '🧑'], ['chunbo', '👮'],
  ['xiaohua', '😅'], ['xiaomei', '📚'], ['azhe', '😜'], ['xiaoqiang', '⚽'], ['xiaofang', '🎀'],
];

function bar(key: AffectionKey, icon: string, family = false): HTMLElement {
  const v = Math.round(S().affection[key] ?? 0);
  const tiers = family ? m().classroom.loveFamily : m().classroom.love;
  const tier = tiers[Math.min(tiers.length - 1, Math.floor(v / (100 / tiers.length)))];
  const hue = v >= 75 ? '#ff7eb6' : v >= 40 ? '#ffb347' : '#8fa3b8';
  return h(
    'div',
    { class: 'cls-j-row' },
    h('span', { class: 'cls-j-icon' }, icon),
    h(
      'div',
      { class: 'cls-j-main' },
      h('div', { class: 'cls-j-name' }, h('b', null, npcName(key)), h('small', null, tier)),
      h('div', { class: 'cls-j-bar', role: 'meter', 'aria-valuemin': '0', 'aria-valuemax': '100', 'aria-valuenow': String(v), 'aria-label': npcName(key) }, h('i', { style: `width:${v}%;background:${hue}` })),
    ),
    h('b', { class: 'cls-j-val' }, String(v)),
  );
}

function people(): HTMLElement[] {
  const ui = m().classroom.ui;
  return [h('h4', null, `🏠 ${ui.family}`), ...FAMILY.map(([k, i]) => bar(k, i, true)), h('h4', null, `🏫 ${ui.classmates}`), ...FRIENDS.map(([k, i]) => bar(k, i))];
}

function log(): HTMLElement[] {
  const list = [...S().journal].reverse();
  if (!list.length) return [h('p', { class: 'cls-j-empty' }, m().classroom.ui.noLog)];
  const out: HTMLElement[] = [];
  let last = NaN;
  for (const e of list) {
    if (e.day !== last) {
      last = e.day;
      out.push(h('h4', null, dayLabel(e.day)));
    }
    out.push(h('div', { class: 'cls-j-entry' }, h('span', { class: 'cls-j-icon' }, e.icon), h('span', null, journalText(e))));
  }
  return out;
}

function hobbies(): HTMLElement[] {
  const ui = m().classroom.ui;
  const names = m().classroom.hobbies;
  const list = Object.entries(S().hobbies).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  if (!list.length) return [h('p', { class: 'cls-j-empty' }, ui.noHobbies)];
  return list.map(([k, v]) => {
    const lv = hobbyLevel(v);
    // Progress towards the next level, from the inverse of hobbyLevel.
    const need = (n: number) => Math.ceil(((n - 1) * (n - 1)) / 1.5);
    const lo = need(lv), hi = need(lv + 1);
    const pct = lv >= 10 ? 100 : Math.round(((v - lo) / Math.max(1, hi - lo)) * 100);
    return h(
      'div',
      { class: 'cls-j-row' },
      h(
        'div',
        { class: 'cls-j-main' },
        h('div', { class: 'cls-j-name' }, h('b', null, names[k] ?? k), h('small', null, ui.hobbyPoints(v))),
        h('div', { class: 'cls-j-bar' }, h('i', { style: `width:${pct}%;background:#74c0ff` })),
      ),
      h('b', { class: 'cls-j-val' }, ui.hobbyLevel(lv)),
    );
  });
}

type Tab = 'people' | 'log' | 'hobbies';
let tab: Tab = 'people';

const CSS = `
.cls-j-tabs{display:flex;gap:6px;justify-content:center;margin-bottom:8px;flex-wrap:wrap}
.cls-j-row{display:flex;align-items:center;gap:8px;padding:6px 2px;border-bottom:1px solid var(--line-soft,rgba(255,255,255,.08))}
.cls-j-icon{width:24px;text-align:center;font-size:18px;flex-shrink:0}
.cls-j-main{flex:1;min-width:0}
.cls-j-name{display:flex;justify-content:space-between;gap:6px;font-size:13px}
.cls-j-name small{opacity:.7;font-size:11px;white-space:nowrap}
.cls-j-bar{height:8px;border-radius:5px;background:rgba(255,255,255,.1);overflow:hidden;margin-top:4px}
.cls-j-bar i{display:block;height:100%;border-radius:5px;transition:width .4s}
.cls-j-val{min-width:34px;text-align:right;font-size:12px;font-variant-numeric:tabular-nums}
.cls-j-entry{display:flex;gap:8px;align-items:flex-start;padding:6px 2px;font-size:13px;line-height:1.35;border-bottom:1px solid var(--line-soft,rgba(255,255,255,.08))}
.cls-j-empty{text-align:center;opacity:.75;font-size:13px;padding:14px 6px;line-height:1.4}
.cls-j-today{margin-top:10px;text-align:left}
.cls-j-today .cls-j-entry{font-size:12px;padding:4px 2px}
`;
let styled = false;
export function journalStyles(): void {
  if (styled || typeof document === 'undefined') return;
  styled = true;
  document.head.appendChild(h('style', null, CSS));
}

export function openJournal(start?: Tab): void {
  journalStyles();
  if (start) tab = start;
  openPanel('cls_journal', () => {
    const ui = m().classroom.ui;
    const chip = (id: Tab, label: string) =>
      h('button', { class: `chip${tab === id ? ' on' : ''}`, 'aria-pressed': String(tab === id), onclick: () => (sfx('click'), (tab = id), refreshPanel('cls_journal')) }, label);
    return {
      title: ui.journalTitle,
      className: 'cls-journal',
      body: [
        h('div', { class: 'cls-j-tabs' }, chip('people', `💞 ${ui.tabPeople}`), chip('log', `📝 ${ui.tabLog}`), chip('hobbies', `🎯 ${ui.tabHobbies}`)),
        tab === 'people' ? people() : tab === 'log' ? log() : hobbies(),
      ],
    };
  });
}

/** Today's moments, for the end-of-day summary. */
export function todayBlock(): HTMLElement | null {
  const list = todayEntries();
  if (!list.length) return null;
  journalStyles();
  return h(
    'div',
    { class: 'cls-j-today' },
    h('h4', null, `📔 ${m().classroom.ui.todayRow}`),
    list.slice(-6).map((e) => h('div', { class: 'cls-j-entry' }, h('span', { class: 'cls-j-icon' }, e.icon), h('span', null, journalText(e)))),
  );
}
